import { useTracker } from '@jetshop/core/analytics/Analytics';
import { trackListEvent } from '@jetshop/core/analytics/tracking';
import React, { useEffect, useMemo } from 'react';
import { css, cx } from 'linaria';
import { ProductCard } from './ProductCard';
import { priceStyle } from './ProductGrid';
import { WindowGrid } from '@jetshop/ui/WindowGrid';
import { theme } from '../Theming/Theming';
import AddToCart from '../Cart/AddToCart';
import CartIcon from '../ui/icons/CartIcon';
import ProductLink from '@jetshop/ui/ProductLink';

const container = css`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 0 -0.5em 1em;
  ${theme.above.lg} {
    margin: 0 -1em 1em;
  }
`;

const defaultStyle = css`
  flex: 0 0 auto;
  width: 25%;
  .product-card-inner {
    padding: 0.5em;
    ${theme.above.lg} {
      padding: 1em;
    }
    .buttons {
      position: absolute;
      right: 1em;
      bottom: 1.5em;
      > * {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: ${theme.colors.primary};
        svg {
          font-size: 1.2rem;
          color: white;
        }
      }
    }
  }
`;

function ProductGridWindow({
  id,
  products,
  listName,
  loading,
  className,
  prevOffset,
  itemsPerRow,
  categoryPath,
  ...rest
}) {
  // Track product listing
  const track = useTracker();
  useEffect(() => {
    // Dont track anything if there are no products to render
    if (!products || products.length === 0) return;

    // Otherwise track a list view event
    track(trackListEvent({ listName, products }));
  }, [listName, products, track]);

  const renderProduct = useMemo(
    () =>
      ({ item: product, style, innerRef }) =>
        (
          <div className={defaultStyle} style={style} ref={innerRef}>
            <ProductCard
              key={product.articleNumber}
              product={product}
              as="div"
              categoryPath={categoryPath}
              loadImageEagerly={product.index < 4}
            >
              <div className="buttons">
                {!product.hasVariants &&
                product.stockStatus &&
                product.stockStatus.buyable &&
                !product.isPackage ? (
                  <AddToCart
                    product={product}
                    quantity={1}
                    selectedVariation={product}
                  >
                    {(add) => (
                      <button onClick={() => add()}>
                        <CartIcon />
                      </button>
                    )}
                  </AddToCart>
                ) : (
                  <ProductLink product={product} categoryPath={null}>
                    <CartIcon />
                  </ProductLink>
                )}
              </div>
            </ProductCard>
          </div>
        ),
    [categoryPath]
  );

  const items = useMemo(() => {
    return products.map((product, index) => ({ ...product, index }));
  }, [products]);

  return (
    <WindowGrid
      id={id}
      itemsPerRow={[2, null, 3, 4]}
      items={items}
      prevOffset={prevOffset}
      className={cx(container, priceStyle)}
      component={renderProduct}
    />
  );
}

export default ProductGridWindow;
